import { PortfolioAsset } from './portfolio.model';
import { PortfolioSortCriterion, PortfolioSortDirection } from './user-setting.model';

/**
 * The sort criteria as the sheet lists them, in the handoff's order.
 *
 * Labelled here rather than in the sheet's template so the column header and
 * the sheet name the same criterion the same way.
 */
export const PORTFOLIO_SORT_CRITERION_LABELS: Readonly<Record<PortfolioSortCriterion, string>> = {
	symbolName: 'نام نماد',
	quantity: 'تعداد دارایی',
	netSaleValue: 'ارزش فعلی',
	profitLoss: 'سود و زیان فعلی',
	profitLossPercent: 'سود و زیان فعلی (درصد سود و زیان)'
};

/**
 * What the direction toggle reads for a number: زیاد به کم / کم به زیاد.
 */
const NUMERIC_DIRECTION_LABELS: Readonly<Record<PortfolioSortDirection, string>> = {
	asc: 'کم به زیاد',
	desc: 'زیاد به کم'
};

/**
 * And for the symbol name, where "more" and "less" mean nothing — scenario 2 of
 * the handoff asks for the alphabet instead.
 */
const NAME_DIRECTION_LABELS: Readonly<Record<PortfolioSortDirection, string>> = {
	asc: 'الف تا ی',
	desc: 'ی تا الف'
};

/** The toggle's wording for `criterion`; only the labels change, never the stored value. */
export function getSortDirectionLabels(
	criterion: PortfolioSortCriterion
): Readonly<Record<PortfolioSortDirection, string>> {
	return criterion === 'symbolName' ? NAME_DIRECTION_LABELS : NUMERIC_DIRECTION_LABELS;
}

/**
 * Compares two holdings on `criterion`, ascending.
 *
 * The symbol name is compared with Persian collation rather than by code point:
 * `localeCompare` puts the alphabet in the order a trader reads it, which code
 * points do not — Arabic-script letters are laid out by shape, and the آ / ا
 * pair alone would sort wrongly without it.
 *
 * Every other criterion is a number, and `symbolName` is the tiebreak for all
 * of them, so two holdings worth the same amount hold a stable relative order
 * across resorts instead of swapping on a tick that changed neither.
 */
function compareAscending(a: PortfolioAsset, b: PortfolioAsset, criterion: PortfolioSortCriterion): number {
	if (criterion === 'symbolName') {
		return a.symbolName.localeCompare(b.symbolName, 'fa');
	}

	return a[criterion] - b[criterion] || a.symbolName.localeCompare(b.symbolName, 'fa');
}

/**
 * The holdings in the trader's chosen order — a new array, so the caller's is
 * left alone.
 *
 * Ordering the holdings is deliberately separate from *when* that happens: per
 * scenario 4 the list must not reshuffle under the trader's thumb as prices
 * tick, so the screen calls this at the moments a resort is expected (opening
 * the screen, a refresh, choosing a sort) and holds the result still in
 * between.
 */
export function sortPortfolioAssets(
	assets: readonly PortfolioAsset[],
	criterion: PortfolioSortCriterion,
	direction: PortfolioSortDirection
): PortfolioAsset[] {
	const sign = direction === 'desc' ? -1 : 1;

	return [...assets].sort((a, b) => sign * compareAscending(a, b, criterion));
}
